import { Component } from '@theme/component';

// ---------------------------------------------------------------------------
// Constants — H is fixed, W is dynamic based on container aspect ratio
// ---------------------------------------------------------------------------

const H = 256;
const MIN_W = 144;

const GRAVITY = 0.19;
const FLAP_VEL = -3.0;
const MAX_VEL = 4.2;

const PIPE_SPEED = 1.5;
const PIPE_WIDTH = 20;
const PIPE_GAP = 52;
const PIPE_SPAWN_INTERVAL = 100; // frames

const GROUND_H = 20;
const GROUND_SPEED = 1.5;

const SKYLINE_FAR_SPEED = 0.3;
const SKYLINE_NEAR_SPEED = 0.6;
const SKYLINE_STRIP_W = 300;

const SKYLINE_FAR = [
  { x: 2, w: 12, h: 30, spire: 0 },
  { x: 20, w: 10, h: 25, spire: 0 },
  { x: 38, w: 14, h: 42, spire: 0 },
  { x: 60, w: 10, h: 28, spire: 0 },
  { x: 78, w: 12, h: 36, spire: 0 },
  { x: 98, w: 16, h: 45, spire: 0 },
  { x: 122, w: 10, h: 25, spire: 0 },
  { x: 142, w: 14, h: 38, spire: 0 },
  { x: 168, w: 12, h: 32, spire: 0 },
  { x: 190, w: 10, h: 22, spire: 0 },
  { x: 210, w: 14, h: 35, spire: 0 },
  { x: 240, w: 12, h: 28, spire: 0 },
];

const SKYLINE_NEAR = [
  { x: 0, w: 10, h: 45, spire: 0 },
  { x: 12, w: 8, h: 35, spire: 0 },
  { x: 22, w: 12, h: 60, spire: 0 },
  { x: 36, w: 18, h: 130, spire: 0, type: 'willis' },   // Willis Tower
  { x: 54, w: 10, h: 50, spire: 0 },
  { x: 66, w: 14, h: 72, spire: 0 },
  { x: 82, w: 12, h: 85, spire: 0 },
  { x: 96, w: 16, h: 92, spire: 4 },
  { x: 114, w: 16, h: 100, spire: 0, type: 'hancock' },  // Hancock
  { x: 132, w: 10, h: 62, spire: 0 },
  { x: 148, w: 12, h: 78, spire: 3 },
  { x: 168, w: 14, h: 55, spire: 0 },
  { x: 190, w: 12, h: 42, spire: 2 },
  { x: 216, w: 16, h: 75, spire: 0 },
  { x: 244, w: 14, h: 48, spire: 0 },
];

const BIRD_X = 30;
const BIRD_SIZE = 14;

const TILT_UP = -0.4;
const TILT_DOWN = Math.PI / 4;

const State = {
  IDLE: 0,
  PLAYING: 1,
  GAME_OVER: 2,
  REWARD: 3,
};

const STORAGE_PREFIX = 'copy-bird';

// ---------------------------------------------------------------------------
// CopyBirdGame — pure game engine (no DOM)
// ---------------------------------------------------------------------------

class CopyBirdGame {
  state = State.IDLE;
  bird = { y: H / 2, vel: 0, tilt: 0 };
  pipes = [];
  groundOffset = 0;
  score = 0;
  bestScore = 0;
  frame = 0;
  gameOverFrame = 0;
  gameOverLetters = null;
  spriteImg = null;
  scoreThreshold = 10;
  hasReward = true;

  // dynamic width — updated by component on resize
  w = MIN_W;

  #skylineFarOffset = 0;
  #skylineNearOffset = 0;
  #skylineFarCanvas = null;
  #skylineNearCanvas = null;

  constructor(spriteImg, scoreThreshold, hasReward, bunnyImg) {
    this.spriteImg = spriteImg;
    this.bunnyImg = bunnyImg;
    this.scoreThreshold = scoreThreshold;
    this.hasReward = hasReward;
    this.#buildSkylineBuffers();
  }

  // --- state transitions ---------------------------------------------------

  reset() {
    this.bird.y = H / 2;
    this.bird.vel = 0;
    this.bird.tilt = 0;
    this.pipes = [];
    this.score = 0;
    this.frame = 0;
    this.gameOverFrame = 0;
    this.gameOverLetters = null;
    this.#skylineFarOffset = 0;
    this.#skylineNearOffset = 0;
    this.state = State.IDLE;
  }

  start() {
    this.bird.y = H / 2;
    this.bird.vel = FLAP_VEL;
    this.bird.tilt = TILT_UP;
    this.pipes = [];
    this.score = 0;
    this.frame = 0;
    this.state = State.PLAYING;
  }

  flap() {
    if (this.state === State.IDLE) {
      this.start();
      return;
    }
    if (this.state === State.PLAYING) {
      this.bird.vel = FLAP_VEL;
      return;
    }
    // GAME_OVER or REWARD — ignore, restart via button only
  }

  // --- per-frame update ----------------------------------------------------

  update() {
    this.groundOffset = (this.groundOffset + GROUND_SPEED) % 8;

    if (this.state === State.IDLE) {
      // gentle bob
      this.bird.y = H / 2 + Math.sin(this.frame * 0.06) * 6;
      this.bird.tilt = 0;
      this.frame++;
      return;
    }

    if (this.state !== State.PLAYING) {
      // GAME_OVER / REWARD — let bird fall to ground
      if (this.bird.y < H - GROUND_H - BIRD_SIZE / 2) {
        this.bird.vel += GRAVITY;
        this.bird.vel = Math.min(this.bird.vel, MAX_VEL);
        this.bird.y += this.bird.vel;
        this.bird.tilt = TILT_DOWN;
      }
      this.gameOverFrame++;

      // --- physics for falling "GAME OVER" letters ---
      if (this.gameOverLetters) {
        const LETTER_DELAY = 60;
        const LETTER_STAGGER = 12;
        const LETTER_GRAVITY = 0.15;
        const BOUNCE_DAMPING = 0.55;
        const SETTLE_THRESHOLD = 0.5;
        const GROUND_Y = H - GROUND_H;
        const CHAR_H = 12;
        const boxX = this.w / 2 - 30;
        const boxY = H / 4 + 14;
        const boxW = 60;

        for (let i = 0; i < this.gameOverLetters.length; i++) {
          const L = this.gameOverLetters[i];
          if (L.settled) continue;
          if (this.gameOverFrame < LETTER_DELAY + i * LETTER_STAGGER) continue;

          // apply gravity
          L.vy += LETTER_GRAVITY;
          L.y += L.vy;
          L.x += L.vx;
          L.rot += L.rotVel;

          // collision with score box top
          const letterLeft = L.x;
          const letterRight = L.x + L.charW;
          const letterBottom = L.y + CHAR_H / 2;
          const overlapsBoxX = letterRight > boxX && letterLeft < boxX + boxW;

          if (overlapsBoxX && L.vy > 0 && letterBottom >= boxY && L.y + CHAR_H / 2 - L.vy < boxY) {
            L.y = boxY - CHAR_H / 2;
            L.vy = -L.vy * BOUNCE_DAMPING;
            L.rotVel += (Math.random() - 0.5) * 0.1;
            // push letter horizontally toward nearest box edge so it slides off
            const letterCenterX = L.x + L.charW / 2;
            const boxCenterX = boxX + boxW / 2;
            if (letterCenterX < boxCenterX) {
              L.vx = -0.5 - Math.random() * 0.3;
            } else {
              L.vx = 0.5 + Math.random() * 0.3;
            }
          }

          // collision with score box sides (left and right)
          const letterBottom2 = L.y + CHAR_H / 2;
          const letterTop2 = L.y - CHAR_H / 2;
          const overlapsBoxY = letterBottom2 > boxY && letterTop2 < boxY + 52;
          if (overlapsBoxY) {
            // left side collision
            if (L.vx > 0 && letterRight > boxX && letterRight - L.vx <= boxX) {
              L.x = boxX - L.charW;
              L.vx = -L.vx * BOUNCE_DAMPING;
            }
            // right side collision
            if (L.vx < 0 && letterLeft < boxX + boxW && letterLeft - L.vx >= boxX + boxW) {
              L.x = boxX + boxW;
              L.vx = -L.vx * BOUNCE_DAMPING;
            }
          }

          // collision with ground
          if (L.y + CHAR_H / 2 >= GROUND_Y) {
            L.y = GROUND_Y - CHAR_H / 2;
            L.vy = -L.vy * BOUNCE_DAMPING;
            L.vx *= 0.8;
            L.rotVel *= 0.8;
            if (Math.abs(L.vy) < SETTLE_THRESHOLD) {
              L.vy = 0;
              L.vx = 0;
              L.rotVel = 0;
              L.settled = true;
            }
          }
        }
      }

      this.frame++;
      return;
    }

    // --- PLAYING ---

    // skyline scroll
    this.#skylineFarOffset = (this.#skylineFarOffset + SKYLINE_FAR_SPEED) % SKYLINE_STRIP_W;
    this.#skylineNearOffset = (this.#skylineNearOffset + SKYLINE_NEAR_SPEED) % SKYLINE_STRIP_W;

    // bird physics
    this.bird.vel += GRAVITY;
    this.bird.vel = Math.min(this.bird.vel, MAX_VEL);
    this.bird.y += this.bird.vel;

    // tilt
    this.bird.tilt = this.bird.vel * 0.1;
    this.bird.tilt = Math.max(TILT_UP, Math.min(TILT_DOWN, this.bird.tilt));

    // spawn pipes
    this.frame++;
    if (this.frame % PIPE_SPAWN_INTERVAL === 0) {
      this.#spawnPipe();
    }

    // move pipes + score
    for (let i = this.pipes.length - 1; i >= 0; i--) {
      const p = this.pipes[i];
      p.x -= PIPE_SPEED;

      if (!p.scored && p.x + PIPE_WIDTH < BIRD_X) {
        p.scored = true;
        this.score++;
      }

      if (p.x + PIPE_WIDTH < 0) {
        this.pipes.splice(i, 1);
      }
    }

    // collision
    if (this.#checkCollision()) {
      this.#die();
    }
  }

  // --- rendering -----------------------------------------------------------

  render(ctx) {
    const w = this.w;

    // clear
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, H);

    // skyline background
    this.#drawSkyline(ctx);

    // pipes
    this.#drawPipes(ctx);

    // ground
    this.#drawGround(ctx, w);

    // bird
    this.#drawBird(ctx);

    // UI overlay
    this.#drawUI(ctx, w);
  }

  // --- private helpers -----------------------------------------------------

  #spawnPipe() {
    const minTop = 24;
    const maxTop = H - GROUND_H - PIPE_GAP - 24;
    const topH = minTop + Math.floor(Math.random() * (maxTop - minTop));
    this.pipes.push({ x: this.w, topH, scored: false });
  }

  #checkCollision() {
    const r = BIRD_SIZE / 2 - 3; // forgiving hitbox
    const by = this.bird.y;

    // floor / ceiling
    if (by + r >= H - GROUND_H) return true;
    if (by - r <= 0) return true;

    // pipes
    for (const p of this.pipes) {
      if (BIRD_X + r > p.x && BIRD_X - r < p.x + PIPE_WIDTH) {
        if (by - r < p.topH || by + r > p.topH + PIPE_GAP) {
          return true;
        }
      }
    }
    return false;
  }

  #die() {
    if (this.score > this.bestScore) {
      this.bestScore = this.score;
    }
    this.gameOverFrame = 0;
    this.gameOverLetters = null;
    this.state =
      this.hasReward && this.scoreThreshold > 0 && this.score >= this.scoreThreshold
        ? State.REWARD
        : State.GAME_OVER;
  }

  // --- draw helpers --------------------------------------------------------

  #drawPipes(ctx) {
    ctx.fillStyle = '#fff';
    for (const p of this.pipes) {
      // top pipe
      ctx.fillRect(p.x, 0, PIPE_WIDTH, p.topH);
      // top pipe cap
      ctx.fillRect(p.x - 2, p.topH - 6, PIPE_WIDTH + 4, 6);

      // bottom pipe
      const bottomY = p.topH + PIPE_GAP;
      ctx.fillRect(p.x, bottomY, PIPE_WIDTH, H - GROUND_H - bottomY);
      // bottom pipe cap
      ctx.fillRect(p.x - 2, bottomY, PIPE_WIDTH + 4, 6);
    }
  }

  #drawGround(ctx, w) {
    const gy = H - GROUND_H;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, gy, w, GROUND_H);

    // hatch pattern
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    const spacing = 8;
    const off = this.groundOffset % spacing;
    for (let x = -GROUND_H - off; x < w + spacing; x += spacing) {
      ctx.beginPath();
      ctx.moveTo(x, gy + 2);
      ctx.lineTo(x + GROUND_H, gy + GROUND_H);
      ctx.stroke();
    }

    // top border
    ctx.fillStyle = '#888';
    ctx.fillRect(0, gy, w, 2);
  }

  #drawBird(ctx) {
    ctx.save();
    ctx.translate(BIRD_X, this.bird.y);
    ctx.rotate(this.bird.tilt);

    if (this.spriteImg) {
      const sw = BIRD_SIZE * 2;
      const sh = BIRD_SIZE;
      ctx.drawImage(this.spriteImg, -sw / 2, -sh / 2, sw, sh);
    } else {
      // fallback: simple pixel bird
      ctx.fillStyle = '#fff';
      // body
      ctx.fillRect(-5, -4, 10, 8);
      // wing
      ctx.fillRect(-7, -2, 4, 4);
      // beak
      ctx.fillRect(5, -1, 3, 2);
      // eye
      ctx.fillStyle = '#000';
      ctx.fillRect(2, -3, 2, 2);
    }

    ctx.restore();
  }

  #drawUI(ctx, w) {
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (this.state === State.IDLE) {
      // title
      ctx.font = 'bold 14px monospace';
      ctx.fillText('COPY BIRD', w / 2, H / 4);

      // prompt (blink)
      if (Math.floor(this.frame / 30) % 2 === 0) {
        ctx.font = '6px monospace';
        ctx.fillText('TAP TO START', w / 2, H / 2 + 30);
      }

      // best score
      if (this.bestScore > 0) {
        ctx.font = '5px monospace';
        ctx.globalAlpha = 0.5;
        ctx.fillText('BEST: ' + this.bestScore, w / 2, H / 2 + 45);
        ctx.globalAlpha = 1;
      }
    }

    if (this.state === State.PLAYING) {
      ctx.font = 'bold 16px monospace';
      ctx.fillText(String(this.score), w / 2, 36);
    }

    if (this.state === State.GAME_OVER || this.state === State.REWARD) {
      const text = 'GAME OVER';
      const LETTER_DELAY = 40;
      const LETTER_STAGGER = 12;
      const CHAR_H = 12;

      ctx.font = 'bold 12px monospace';
      const textW = ctx.measureText(text).width;
      const charW = textW / text.length;
      const baseX = w / 2 - textW / 2;
      const baseY = H / 4;

      // --- lazy-init letter physics on first render frame ---
      if (!this.gameOverLetters) {
        this.gameOverLetters = [];
        for (let i = 0; i < text.length; i++) {
          if (text[i] === ' ') continue;
          this.gameOverLetters.push({
            ch: text[i],
            x: baseX + i * charW,
            y: baseY,
            origX: baseX + i * charW,
            origY: baseY,
            vx: 0,
            vy: 0,
            rot: 0,
            rotVel: (Math.random() - 0.5) * 0.08,
            charW: charW,
            settled: false,
          });
        }
      }

      // --- score box (drawn immediately, no fade) ---
      const boxX = w / 2 - 30;
      const boxY = H / 4 + 14;
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.strokeRect(boxX, boxY, 60, 52);

      ctx.font = '5px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('SCORE', w / 2, boxY + 10);
      ctx.font = 'bold 14px monospace';
      ctx.fillText(String(this.score), w / 2, boxY + 22);

      ctx.fillStyle = '#888';
      ctx.fillRect(boxX + 4, boxY + 28, 52, 1);
      ctx.fillStyle = '#fff';

      ctx.font = '5px monospace';
      ctx.fillText('BEST', w / 2, boxY + 35);
      ctx.font = '9px monospace';
      ctx.fillText(String(this.bestScore), w / 2, boxY + 43);

      // --- draw letters ---
      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      for (let i = 0; i < this.gameOverLetters.length; i++) {
        const L = this.gameOverLetters[i];
        const released = this.gameOverFrame >= LETTER_DELAY + i * LETTER_STAGGER;

        if (!released) {
          // draw at original static position (no rotation)
          ctx.fillText(L.ch, L.origX, L.origY);
        } else {
          // draw at physics position with rotation
          ctx.save();
          ctx.translate(L.x + L.charW / 2, L.y);
          ctx.rotate(L.rot);
          ctx.fillText(L.ch, -L.charW / 2, 0);
          ctx.restore();
        }
      }

      // restore text alignment
      ctx.textAlign = 'center';
    }
  }

  #buildSkylineBuffers() {
    const groundY = H - GROUND_H;

    // --- far layer ---
    const farCvs = document.createElement('canvas');
    farCvs.width = SKYLINE_STRIP_W;
    farCvs.height = H;
    const farCtx = farCvs.getContext('2d');
    farCtx.imageSmoothingEnabled = false;

    farCtx.fillStyle = '#111';
    for (const b of SKYLINE_FAR) {
      const by = groundY - b.h;
      farCtx.fillRect(b.x, by, b.w, b.h);
      if (b.spire > 0) {
        farCtx.fillRect(b.x + Math.floor(b.w / 2), by - b.spire, 1, b.spire);
      }
    }

    this.#skylineFarCanvas = farCvs;

    // --- near layer ---
    const nearCvs = document.createElement('canvas');
    nearCvs.width = SKYLINE_STRIP_W;
    nearCvs.height = H;
    const nearCtx = nearCvs.getContext('2d');
    nearCtx.imageSmoothingEnabled = false;

    // Pick a random generic building for the bunny logo easter egg
    const bunnyEligible = SKYLINE_NEAR.filter(b => !b.type && b.w >= 12 && b.h >= 50);
    const bunnyTarget = bunnyEligible.length > 0
      ? bunnyEligible[Math.floor(Math.random() * bunnyEligible.length)]
      : null;

    // buildings
    for (const b of SKYLINE_NEAR) {
      const by = groundY - b.h;

      if (b.type === 'willis') {
        // ---------------------------------------------------------------
        // Willis Tower — stepped bundled-tube silhouette
        // Real building: 9 square tubes in 3x3 grid, with setbacks at
        // floors 50, 66, and 90 as tube bundles terminate. Twin antennas.
        // ---------------------------------------------------------------
        const cx = b.x + b.w / 2;
        const tiers = [
          { frac: 0.38, w: b.w },                       // floors 1-49:  all 9 tubes
          { frac: 0.22, w: b.w - 4 },                   // floors 50-65: 7 tubes
          { frac: 0.24, w: Math.max(b.w - 8, 6) },      // floors 66-90: 5 tubes
          { frac: 0.16, w: Math.max(b.w - 12, 4) },     // floors 91-108: 2 tubes
        ];

        // Tier bodies (bottom to top)
        nearCtx.fillStyle = '#1a1a1a';
        let tierY = groundY;
        const tierCoords = [];
        for (const t of tiers) {
          const th = Math.round(b.h * t.frac);
          const tx = Math.round(cx - t.w / 2);
          nearCtx.fillRect(tx, tierY - th, t.w, th);
          tierCoords.push({ tx, ty: tierY - th, tw: t.w, th });
          tierY -= th;
        }

        // Ledge overhangs at each setback — 1px lip emphasizes the steps
        nearCtx.fillStyle = '#282828';
        for (let i = 1; i < tierCoords.length; i++) {
          const below = tierCoords[i - 1];
          const above = tierCoords[i];
          nearCtx.fillRect(below.tx, above.ty + above.th, below.tw, 1);
        }

        // Twin antennas (west is slightly taller, matching reality)
        nearCtx.fillStyle = '#1a1a1a';
        nearCtx.fillRect(Math.round(cx - 3), tierY - 10, 1, 10);
        nearCtx.fillRect(Math.round(cx + 2), tierY - 8, 1, 8);

        // Window dots per tier
        nearCtx.fillStyle = '#333';
        for (const tc of tierCoords) {
          for (let wy = tc.ty + 3; wy < tc.ty + tc.th - 1; wy += 4) {
            for (let wx = tc.tx + 2; wx < tc.tx + tc.tw - 1; wx += 3) {
              nearCtx.fillRect(wx, wy, 1, 1);
            }
          }
        }

      } else if (b.type === 'hancock') {
        // ---------------------------------------------------------------
        // John Hancock Center — tapered trapezoid with X-bracing
        // Real building: 100-story trapezoidal tower with signature
        // exterior diagonal cross-bracing and a tall broadcast antenna.
        // ---------------------------------------------------------------
        const cx = b.x + b.w / 2;
        const baseW = b.w;
        const topW = Math.round(b.w * 0.625);   // tapers to ~62.5% at top
        const topY = groundY - b.h;

        // Tapered body (filled trapezoid)
        nearCtx.fillStyle = '#1a1a1a';
        nearCtx.beginPath();
        nearCtx.moveTo(Math.round(cx - baseW / 2), groundY);
        nearCtx.lineTo(Math.round(cx - topW / 2), topY);
        nearCtx.lineTo(Math.round(cx + topW / 2), topY);
        nearCtx.lineTo(Math.round(cx + baseW / 2), groundY);
        nearCtx.closePath();
        nearCtx.fill();

        // Tall broadcast antenna
        nearCtx.fillRect(Math.round(cx), topY - 14, 1, 14);

        // X-bracing — the Hancock's signature exterior cross-braces
        nearCtx.strokeStyle = '#2a2a2a';
        nearCtx.lineWidth = 1;
        const braceCount = 5;
        const sectionH = b.h / braceCount;
        for (let i = 0; i < braceCount; i++) {
          const secTop = topY + i * sectionH;
          const secBot = secTop + sectionH;
          const fracTop = i / braceCount;
          const fracBot = (i + 1) / braceCount;
          const wTop = topW + (baseW - topW) * fracTop;
          const wBot = topW + (baseW - topW) * fracBot;

          // diagonal from top-left to bottom-right
          nearCtx.beginPath();
          nearCtx.moveTo(cx - wTop / 2 + 1, secTop);
          nearCtx.lineTo(cx + wBot / 2 - 1, secBot);
          nearCtx.stroke();

          // diagonal from top-right to bottom-left
          nearCtx.beginPath();
          nearCtx.moveTo(cx + wTop / 2 - 1, secTop);
          nearCtx.lineTo(cx - wBot / 2 + 1, secBot);
          nearCtx.stroke();
        }

        // Window dots following the taper
        nearCtx.fillStyle = '#333';
        for (let wy = topY + 3; wy < groundY - 2; wy += 4) {
          const frac = (wy - topY) / b.h;
          const rowW = topW + (baseW - topW) * frac;
          const rowLeft = cx - rowW / 2;
          for (let wx = rowLeft + 2; wx < rowLeft + rowW - 2; wx += 3) {
            nearCtx.fillRect(Math.round(wx), wy, 1, 1);
          }
        }

      } else {
        // Generic building
        nearCtx.fillStyle = '#1a1a1a';
        nearCtx.fillRect(b.x, by, b.w, b.h);

        if (b.spire > 0) {
          nearCtx.fillRect(b.x + Math.floor(b.w / 2), by - b.spire, 1, b.spire);
        }

        nearCtx.fillStyle = '#333';
        for (let wy = by + 2; wy < groundY - 2; wy += 4) {
          for (let wx = b.x + 2; wx < b.x + b.w - 2; wx += 3) {
            nearCtx.fillRect(wx, wy, 1, 1);
          }
        }

        // Playboy bunny logo easter egg — actual PNG drawn at low res
        if (b === bunnyTarget && this.bunnyImg) {
          const aspect = this.bunnyImg.height / this.bunnyImg.width;
          const drawW = Math.min(b.w - 2, 16);
          const drawH = Math.round(drawW * aspect);
          const dx = Math.round(b.x + b.w / 2 - drawW / 2);
          const dy = by + 6;
          nearCtx.globalAlpha = 0.22;
          nearCtx.drawImage(this.bunnyImg, dx, dy, drawW, drawH);
          nearCtx.globalAlpha = 1;
        }
      }
    }

    // bridge
    const beamY = groundY - 14;
    nearCtx.fillStyle = '#1a1a1a';
    nearCtx.fillRect(0, beamY, SKYLINE_STRIP_W, 2);

    // truss triangles
    nearCtx.strokeStyle = '#1a1a1a';
    nearCtx.lineWidth = 1;
    for (let x = 0; x < SKYLINE_STRIP_W; x += 16) {
      nearCtx.beginPath();
      nearCtx.moveTo(x, beamY);
      nearCtx.lineTo(x + 8, beamY + 10);
      nearCtx.lineTo(x + 16, beamY);
      nearCtx.stroke();
    }

    // vertical supports
    for (let x = 0; x < SKYLINE_STRIP_W; x += 40) {
      nearCtx.fillRect(x, beamY, 1, groundY - beamY);
    }

    this.#skylineNearCanvas = nearCvs;
  }

  #drawSkyline(ctx) {
    const copies = Math.ceil(this.w / SKYLINE_STRIP_W) + 1;

    // far layer
    if (this.#skylineFarCanvas) {
      for (let i = 0; i < copies; i++) {
        ctx.drawImage(this.#skylineFarCanvas, -this.#skylineFarOffset + i * SKYLINE_STRIP_W, 0);
      }
    }

    // near layer
    if (this.#skylineNearCanvas) {
      for (let i = 0; i < copies; i++) {
        ctx.drawImage(this.#skylineNearCanvas, -this.#skylineNearOffset + i * SKYLINE_STRIP_W, 0);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// CopyBirdComponent — web component shell
// ---------------------------------------------------------------------------

class CopyBirdComponent extends Component {
  requiredRefs = ['canvas'];

  /** @type {CopyBirdGame | null} */
  #game = null;

  /** @type {CanvasRenderingContext2D | null} */
  #ctx = null;

  /** @type {number | null} */
  #raf = null;

  #running = false;

  /** @type {IntersectionObserver | null} */
  #viewportObs = null;

  /** @type {ResizeObserver | null} */
  #resizeObs = null;

  #lastFlapTime = 0;
  #scoreSaved = false;
  #retryTimeout = null;

  #settings = {
    spriteUrl: '',
    discountCode: '',
    scoreThreshold: 10,
    discountDescription: '',
    sectionId: '',
  };

  connectedCallback() {
    super.connectedCallback();
    this.#readSettings();
    this.#setupCanvas();
    Promise.all([this.#loadSprite(), this.#loadBunny()]).then(([spriteImg, bunnyImg]) => {
      const hasReward = Boolean(this.#settings.discountCode);
      this.#game = new CopyBirdGame(spriteImg, this.#settings.scoreThreshold, hasReward, bunnyImg);
      this.#game.bestScore = this.#loadBestScore();
      this.#fitCanvas();
      this.#bindInput();
      this.#startLoop();
      this.#setupViewportObserver();
      this.#setupResizeObserver();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopLoop();
    this.#viewportObs?.disconnect();
    this.#resizeObs?.disconnect();
    this.#unbindInput();
    if (this.#retryTimeout) clearTimeout(this.#retryTimeout);
  }

  // --- settings ------------------------------------------------------------

  #readSettings() {
    this.#settings = {
      spriteUrl: this.dataset.spriteUrl || '',
      discountCode: this.dataset.discountCode || '',
      scoreThreshold: parseInt(this.dataset.scoreThreshold, 10) || 10,
      discountDescription: this.dataset.discountDescription || '',
      sectionId: this.dataset.sectionId || '',
    };
  }

  // --- canvas --------------------------------------------------------------

  #setupCanvas() {
    const canvas = this.refs.canvas;
    canvas.width = MIN_W;
    canvas.height = H;
    this.#ctx = canvas.getContext('2d');
    this.#ctx.imageSmoothingEnabled = false;
  }

  #fitCanvas() {
    const container = this.refs.canvas.parentElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    // keep H fixed at 256, calculate W to match container aspect ratio
    // this preserves pixel size — each game pixel is the same physical size
    const scale = rect.height / H;
    const newW = Math.max(MIN_W, Math.round(rect.width / scale));

    const canvas = this.refs.canvas;
    canvas.width = newW;
    canvas.height = H;

    if (this.#ctx) {
      this.#ctx.imageSmoothingEnabled = false;
    }

    if (this.#game) {
      this.#game.w = newW;
    }
  }

  // --- resize observer -----------------------------------------------------

  #setupResizeObserver() {
    this.#resizeObs = new ResizeObserver(() => {
      this.#fitCanvas();
    });
    const container = this.refs.canvas?.parentElement;
    if (container) {
      this.#resizeObs.observe(container);
    }
  }

  // --- sprite loading ------------------------------------------------------

  #loadSprite() {
    return new Promise((resolve) => {
      if (!this.#settings.spriteUrl) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = this.#settings.spriteUrl;
    });
  }

  #loadBunny() {
    return new Promise((resolve) => {
      const url = this.dataset.bunnyUrl;
      if (!url) { resolve(null); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  // --- input ---------------------------------------------------------------

  #bindInput() {
    this.refs.canvas.addEventListener('click', this.#handleClick);
    this.refs.canvas.addEventListener('touchstart', this.#handleTouch, { passive: false });
    document.addEventListener('keydown', this.#handleKey);
  }

  #unbindInput() {
    this.refs.canvas?.removeEventListener('click', this.#handleClick);
    this.refs.canvas?.removeEventListener('touchstart', this.#handleTouch);
    document.removeEventListener('keydown', this.#handleKey);
  }

  #handleClick = (e) => {
    e.preventDefault();
    this.#onFlap();
  };

  #handleTouch = (e) => {
    e.preventDefault();
    this.#onFlap();
  };

  #handleKey = (e) => {
    if (e.code === 'Space' || e.key === ' ') {
      e.preventDefault();
      this.#onFlap();
    }
  };

  #onFlap() {
    if (!this.#game) return;

    // ignore flaps while dead — restart via button only
    if (this.#game.state === State.GAME_OVER || this.#game.state === State.REWARD) return;

    // debounce to prevent touch + click double-fire
    const now = performance.now();
    if (now - this.#lastFlapTime < 100) return;
    this.#lastFlapTime = now;

    this.#game.flap();
  }

  // --- game loop -----------------------------------------------------------

  #loop = () => {
    if (!this.#running || !this.#game || !this.#ctx) return;
    this.#game.update();
    this.#game.render(this.#ctx);

    // handle state transitions from update() (death happens in update, not flap)
    if (!this.#scoreSaved && (this.#game.state === State.GAME_OVER || this.#game.state === State.REWARD)) {
      this.#saveBestScore();
      this.#scoreSaved = true;

      if (this.#game.state === State.REWARD) {
        this.#showReward();
      }

      // show retry button after a short delay so frantic taps don't immediately restart
      this.#retryTimeout = setTimeout(() => {
        this.#showRetry();
      }, 1600);
    }

    this.#raf = requestAnimationFrame(this.#loop);
  };

  #startLoop() {
    if (this.#running) return;
    this.#running = true;
    this.#raf = requestAnimationFrame(this.#loop);
  }

  #stopLoop() {
    this.#running = false;
    if (this.#raf) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
  }

  // --- viewport observer ---------------------------------------------------

  #setupViewportObserver() {
    this.#viewportObs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#startLoop();
          } else {
            this.#stopLoop();
          }
        }
      },
      { rootMargin: '100px' }
    );
    this.#viewportObs.observe(this);
  }

  // --- reward panel --------------------------------------------------------

  #showReward() {
    if (!this.#settings.discountCode) return;
    const { rewardPanel, discountCodeDisplay, rewardDescription } = this.refs;
    if (!rewardPanel) return;
    rewardPanel.hidden = false;
    if (discountCodeDisplay) discountCodeDisplay.textContent = this.#settings.discountCode;
    if (rewardDescription) rewardDescription.textContent = this.#settings.discountDescription;
  }

  #hideReward() {
    if (this.refs.rewardPanel) this.refs.rewardPanel.hidden = true;
  }

  // --- retry button --------------------------------------------------------

  #showRetry() {
    if (!this.refs.retryPanel) return;
    this.refs.retryPanel.hidden = false;
    // rAF so the browser applies hidden=false before transitioning opacity
    requestAnimationFrame(() => {
      this.refs.retryPanel.classList.add('is-visible');
    });
  }

  #hideRetry() {
    if (!this.refs.retryPanel) return;
    this.refs.retryPanel.classList.remove('is-visible');
    this.refs.retryPanel.hidden = true;
    if (this.#retryTimeout) {
      clearTimeout(this.#retryTimeout);
      this.#retryTimeout = null;
    }
  }

  restartGame() {
    if (!this.#game) return;
    if (this.#game.state !== State.GAME_OVER && this.#game.state !== State.REWARD) return;
    this.#hideRetry();
    this.#hideReward();
    this.#scoreSaved = false;
    this.#game.reset();
    this.#game.start();
  }

  copyCode() {
    const code = this.#settings.discountCode;
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      const btn = this.refs.copyButton;
      if (!btn) return;
      btn.textContent = 'COPIED!';
      setTimeout(() => {
        btn.textContent = 'COPY CODE';
      }, 2000);
    }).catch(() => {});
  }

  shareScore() {
    if (!this.#game) return;
    const text = `I scored ${this.#game.score} in Copy Bird! Can you beat me?\n${window.location.href}`;

    if (navigator.share) {
      navigator.share({ text }).catch(() => {
        this.#fallbackCopyText(text);
      });
    } else {
      this.#fallbackCopyText(text);
    }
  }

  #fallbackCopyText(text) {
    navigator.clipboard.writeText(text).then(() => {
      const btn = this.refs.shareButton;
      if (!btn) return;
      btn.textContent = 'COPIED!';
      setTimeout(() => {
        btn.textContent = 'SHARE SCORE';
      }, 2000);
    }).catch(() => {});
  }

  // --- localStorage --------------------------------------------------------

  #key(suffix) {
    return `${STORAGE_PREFIX}-${this.#settings.sectionId}-${suffix}`;
  }

  #loadBestScore() {
    try {
      return parseInt(localStorage.getItem(this.#key('best')) || '0', 10);
    } catch {
      return 0;
    }
  }

  #saveBestScore() {
    if (!this.#game) return;
    if (this.#game.score > this.#game.bestScore) {
      this.#game.bestScore = this.#game.score;
    }
    try {
      localStorage.setItem(this.#key('best'), String(this.#game.bestScore));
    } catch {
      // localStorage unavailable
    }
  }
}

customElements.define('copy-bird-component', CopyBirdComponent);
